import { PRIMARY_AIRPORTS } from "./airport-ranking";

/**
 * Major cities' English names and the other names our data uses for them: the airport data's
 * local spellings ("Ciudad de México", "München") and the venue inventory's ("Roma", "Wien").
 */
const CITY_NAMES: Readonly<Record<string, readonly string[]>> = {
  "Mexico City": ["Ciudad de México", "Ciudad de Mexico", "CDMX", "México D.F."],
  Munich: ["München", "Muenchen"],
  Lisbon: ["Lisboa"],
  Rome: ["Roma"],
  Milan: ["Milano"],
  Cologne: ["Köln", "Koeln"],
  Vienna: ["Wien"],
  Prague: ["Praha"],
  Brussels: ["Bruxelles", "Brussel"],
  Florence: ["Firenze"],
  Naples: ["Napoli"],
  Seville: ["Sevilla"],
  Zurich: ["Zürich"],
  Geneva: ["Genève", "Genf"],
  "São Paulo": ["Sao Paulo"],
  Bogotá: ["Bogota"],
  Copenhagen: ["København"],
  Athens: ["Athína", "Athina"],
  Warsaw: ["Warszawa"],
  Krakow: ["Kraków"],
  Gothenburg: ["Göteborg"],
  Venice: ["Venezia"],
  Turin: ["Torino"],
  "The Hague": ["Den Haag", "'s-Gravenhage"],
  Antwerp: ["Antwerpen"],
  Nuremberg: ["Nürnberg"],
  Hanover: ["Hannover"],
  Montreal: ["Montréal"],
  "Quebec City": ["Québec"],
  Marrakesh: ["Marrakech"],
  Bucharest: ["București", "Bucuresti"],
  Belgrade: ["Beograd"],
  Seoul: ["Seúl"],
};

/** Towns airports are named for, mapped to the city they serve ("Zaventem" is Brussels' airport). */
const AIRPORT_TOWNS: Readonly<Record<string, string>> = {
  gatwick: "London",
  luton: "London",
  "colombier saugnieu": "Lyon",
  marignane: "Marseille",
  paisley: "Glasgow",
  "orio al serio": "Bergamo",
  zaventem: "Brussels",
  "spata artemida": "Athens",
  balice: "Krakow",
  ferno: "Milan",
  "caselle torinese": "Turin",
  schwechat: "Vienna",
  kloten: "Zurich",
  fiumicino: "Rome",
  tessera: "Venice",
};

const fold = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’.]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Folded local name or English name → English name. */
const TO_ENGLISH = new Map<string, string>([
  ...Object.entries(CITY_NAMES).flatMap(([english, locals]) =>
    [english, ...locals].map((name) => [fold(name), english] as const)
  ),
  ...Object.entries(AIRPORT_TOWNS),
]);

/** Cities the airport data may put after a district ("Pendik, Istanbul"), as folded names. */
const KNOWN_CITIES = new Set([...TO_ENGLISH.keys(), ...Object.keys(PRIMARY_AIRPORTS)]);

/**
 * The English name a city goes by, from any spelling our data uses: "Ciudad de México" and
 * "CIUDAD DE MEXICO" are "Mexico City", "Köln (Cologne)" is "Cologne", "Shanghai (Pudong)" is
 * "Shanghai", "Raleigh/Durham" is "Raleigh", "Pendik, Istanbul" is "Istanbul", "Gatwick, Surrey"
 * is "London". A city with no other name keeps its own, tidied.
 * @param name - A city name as typed or stored.
 * @returns The canonical name.
 * @throws Never.
 */
export const canonicalCity = (name: string): string => {
  const trimmed = name.trim().replace(/\s+/g, " ");
  // A parenthetical is an area or airport ("Shanghai (Pudong)"), unless it is the English name.
  const paren = trimmed.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  let base = paren ? paren[1] : trimmed;
  const hint = paren?.[2];
  if (hint && !TO_ENGLISH.has(fold(base)) && TO_ENGLISH.has(fold(hint))) return TO_ENGLISH.get(fold(hint))!;
  // "Raleigh/Durham", "Cincinnati / Covington", "City - Area": the first city names the place.
  base = base.split(/\s*\/\s*|\s+-\s+/)[0];
  // "Birmingham, West Midlands" names a county after the city; "Pendik, Istanbul" a city after a district.
  const parts = base.split(/\s*,\s*/);
  if (parts.length > 1) base = KNOWN_CITIES.has(fold(parts[parts.length - 1])) ? parts[parts.length - 1] : parts[0];
  return TO_ENGLISH.get(fold(base)) ?? base;
};

/**
 * The separate names inside an airport-data city: "Shanghai (Pudong)" gives "Shanghai" and
 * "Pudong", "Raleigh/Durham" gives both, so a planner can find the place by either.
 * @param name - A city name as the airport data spells it.
 * @returns Its parts, without empty ones.
 * @throws Never.
 */
export const cityNameParts = (name: string): string[] =>
  name
    .split(/[(),/]|\s+-\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 2);

/**
 * Every name a city goes by, canonical first, each also without accents, for matching records
 * that spell it another way (the venue inventory lists Rome as both "Rome" and "Roma").
 * @param name - A city name in any spelling.
 * @returns Distinct names, compared ignoring case.
 * @throws Never.
 */
export const cityNames = (name: string): string[] => {
  const canonical = canonicalCity(name);
  const names = [canonical, ...(CITY_NAMES[canonical] ?? []), name.trim()];
  const withPlain = names.flatMap((entry) => [entry, entry.normalize("NFD").replace(/[̀-ͯ]/g, "")]);
  const seen = new Set<string>();
  return withPlain.filter((entry) => {
    const key = entry.toLowerCase();
    if (!entry || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * The key two records about the same city share, whatever spelling each uses.
 * @param name - A city name in any spelling.
 * @returns The folded canonical name.
 * @throws Never.
 */
export const placeKey = (name: string): string => fold(canonicalCity(name));
